/** @file Sampler for deform timelines: blends two sparse offset keys into the attachment's dense buffer. */
import type { TimelineHandler } from '../../contracts/timeline';
import { evalCurve } from '../../curve/evalCurve';
import { locateKey, type KeyCursor } from './locateKey';

const cur: KeyCursor = { k0: 0, k1: 0, u: 0 };

/** dst[start + j] += w * src[j] for every j that fits inside dst (entries outside a key's range count as 0). */
function addKey(dst: Float64Array, src: Float64Array | undefined, start: number, w: number): void {
  if (src === undefined) return;
  const n = Math.min(src.length, dst.length - start);
  for (let j = 0; j < n; j++) dst[start + j] = (dst[start + j] ?? 0) + w * (src[j] ?? 0);
}

/** Writes (1 - f)·key0 + f·key1 into Sample.deform[attachment] (f from the curve, normalized) and marks it keyed. */
export const deformHandler: TimelineHandler<'deform'> = {
  sample(tl, time, lastTime, out) {
    const dst = out.deform[tl.attachment];
    if (dst === undefined || !locateKey(tl.times, time, cur)) return;
    const f = cur.k1 === cur.k0 ? 0 : evalCurve(tl.curves.kind, tl.curves.cp, cur.k0, 0, 1, cur.u);
    dst.fill(0);
    addKey(dst, tl.values[cur.k0], tl.starts[cur.k0] ?? 0, 1 - f);
    if (f !== 0) addKey(dst, tl.values[cur.k1], tl.starts[cur.k1] ?? 0, f);
    out.deformKeyed[tl.attachment] = 1;
  },
};
